import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Grid, Typography, Button, CircularProgress, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ProductCard from '../components/ProductCard';
import ProductCreateDialog from '../components/ProductCreateDialog';
import useProducts from '../hooks/useProducts';
import { useAuth } from '../context/AuthContext';
import { getYourFavoritesPublic, getOurFavoritesPublic, type ProductCard as ProductCardType } from '../services/api';
import { theme } from '../styles/theme';

const PATH_TO_TYPE: Record<string, string[] | null> = {
  '/shop': null,
  '/leather-bags': ['Leather Bag'],
  '/wallets-accessories': ['Wallet', 'Accessory'],
};

const PATH_TO_TITLE: Record<string, string> = {
  '/shop': 'Shop All',
  '/leather-bags': 'Handbags',
  '/wallets-accessories': 'Wallets & Accessories',
  '/your-favorites': 'Your Favorites',
  '/our-favorites': 'Our Favorites',
};

const ShopPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const { products, setProducts } = useProducts();
  const navigate = useNavigate();
  const location = useLocation();
  const isYourFavoritesRoute = location.pathname === '/your-favorites';
  const isOurFavoritesRoute = location.pathname === '/our-favorites';
  const [createOpen, setCreateOpen] = useState(false);
  const [favoritesProducts, setFavoritesProducts] = useState<ProductCardType[] | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!isYourFavoritesRoute && !isOurFavoritesRoute) return;
    let mounted = true;
    setFavoritesProducts(null);
    const fetchFavorites = isYourFavoritesRoute ? getYourFavoritesPublic : getOurFavoritesPublic;
    fetchFavorites()
      .then((list) => {
        if (mounted) setFavoritesProducts(list);
      })
      .catch(() => {
        if (mounted) setFavoritesProducts([]);
      });
    return () => {
      mounted = false;
    };
  }, [isYourFavoritesRoute, isOurFavoritesRoute]);

  const filterType = PATH_TO_TYPE[location.pathname] ?? null;
  const title = PATH_TO_TITLE[location.pathname] ?? 'Shop';

  const filteredProducts = useMemo(() => {
    if (isYourFavoritesRoute || isOurFavoritesRoute) return favoritesProducts ?? [];
    if (!filterType?.length) return products;
    return products.filter(
      (p: ProductCardType) =>
        p.product_type_name != null && filterType.includes(p.product_type_name)
    );
  }, [products, filterType, isYourFavoritesRoute, isOurFavoritesRoute, favoritesProducts]);

  const shopCards = useMemo(() => {
    if (isYourFavoritesRoute || isOurFavoritesRoute) {
      return filteredProducts.map((p, i) => ({
        key: `yf-${i}-${p.id}`,
        product: p,
        imageUrl: p.image_url ?? null,
        colorId: null as number | null,
      }));
    }
    const rows: { key: string; product: ProductCardType; imageUrl: string | null; colorId: number | null }[] = [];
    for (const p of filteredProducts) {
      const urls =
        p.image_urls && p.image_urls.length > 0 ? p.image_urls : p.image_url ? [p.image_url] : [];
      const colorIds = p.image_color_ids ?? [];
      if (urls.length === 0) {
        rows.push({ key: String(p.id), product: p, imageUrl: null, colorId: null });
      } else {
        urls.forEach((url, i) => {
          rows.push({ key: `${p.id}-${i}`, product: p, imageUrl: url, colorId: colorIds[i] ?? null });
        });
      }
    }
    return rows;
  }, [filteredProducts, isYourFavoritesRoute, isOurFavoritesRoute]);

  const openCreate = () => setCreateOpen(true);
  const closeCreate = () => setCreateOpen(false);

  return (
    <Box sx={{ backgroundColor: 'background.paper' }}>
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 3, py: 4, minHeight: 72, maxHeight: 85 }}>
        <Typography variant="h4" sx={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', pointerEvents: 'none' }}>
          {title}
        </Typography>
        {isAdmin() && (
          <Button
            variant="contained"
            color="primary"
            onClick={openCreate}
            aria-label="create-product"
            sx={isMobile ? { minWidth: 40, px: 1 } : undefined}
          >
            {isMobile ? <AddIcon /> : 'Create product'}
          </Button>
        )}
      </Box>

      {(isYourFavoritesRoute || isOurFavoritesRoute) && favoritesProducts === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container>
          {shopCards.map(({ key, product: p, imageUrl, colorId }) => (
            <Grid key={key} item xs={12} sm={6} md={4} lg={3}>
              <ProductCard
                product={{ ...p, image_url: imageUrl }}
                onClick={() => navigate(`/product/${p.id}`, { state: { selectedColorId: colorId } })}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <ProductCreateDialog
        open={createOpen}
        onClose={closeCreate}
        onCreated={(created) => setProducts((prev) => [...prev, created])}
      />
    </Box>
  );
};

export default ShopPage;
